import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Space,
  Typography,
} from "antd";
import { Locale } from "../../utils";
import DeliveryZoneBoundaryMap from "./DeliveryZoneBoundaryMap";

const i18n = Locale.getInstance();
const { Text } = Typography;

function EditDeliveryZoneForm({
  visible,
  zoneData,
  nextLevel,
  shopLongitude,
  shopLatitude,
  onCancel,
  onSubmit,
  loading,
}) {
  const [form] = Form.useForm();
  const [boundary, setBoundary] = useState(null);
  const isEdit = !!(zoneData && zoneData.id);

  useEffect(() => {
    if (!visible) return;
    const initial = zoneData || {};
    form.setFieldsValue({
      level: initial.level || nextLevel || 1,
      zh_name: initial.zh_name || "",
      ug_name: initial.ug_name || "",
      is_free: !!initial.is_free,
      ship_price:
        initial.ship_price != null ? Number(initial.ship_price) / 100 : 0,
      starting_price:
        initial.starting_price != null
          ? Number(initial.starting_price) / 100
          : 0,
      enabled: initial.enabled !== undefined ? !!initial.enabled : true,
    });
    setBoundary(initial.boundary || null);
  }, [visible, zoneData, nextLevel, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!boundary || boundary.length < 3) {
        form.setFields([
          {
            name: "boundary",
            errors: [i18n.t("deliveryZone.boundaryRequired")],
          },
        ]);
        return;
      }
      onSubmit({
        ...values,
        ship_price: values.is_free
          ? 0
          : Math.round(Number(values.ship_price || 0) * 100),
        starting_price: Math.round(Number(values.starting_price || 0) * 100),
        boundary,
      });
    } catch (e) {
      // validation errors shown by form
    }
  };

  return (
    <Modal
      open={visible}
      title={isEdit ? i18n.t("deliveryZone.edit") : i18n.t("deliveryZone.add")}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      width={820}
      destroyOnClose
      okText={i18n.t("common.modal.okText") || "OK"}
      cancelText={i18n.t("common.modal.cancelText") || "Cancel"}
    >
      <Form form={form} layout="vertical">
        <Space size="large" style={{ width: "100%" }} align="start">
          <Form.Item
            name="level"
            label={i18n.t("deliveryZone.level")}
            rules={[{ required: true, message: i18n.t("deliveryZone.levelHint") }]}
            style={{ width: 140 }}
          >
            <InputNumber min={1} style={{ width: "100%" }} disabled={isEdit} />
          </Form.Item>
          <Form.Item
            name="zh_name"
            label={i18n.t("deliveryZone.zhName")}
            style={{ width: 220 }}
          >
            <Input placeholder={i18n.t("deliveryZone.zhNameHint")} />
          </Form.Item>
          <Form.Item
            name="ug_name"
            label={i18n.t("deliveryZone.ugName")}
            style={{ width: 220 }}
          >
            <Input placeholder={i18n.t("deliveryZone.ugNameHint")} />
          </Form.Item>
        </Space>

        <Space size="large" align="center">
          <Form.Item
            name="is_free"
            label={i18n.t("deliveryZone.isFree")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.is_free !== cur.is_free}
          >
            {({ getFieldValue }) =>
              getFieldValue("is_free") ? null : (
                <Form.Item
                  name="ship_price"
                  label={i18n.t("deliveryZone.shipPrice")}
                  rules={[
                    {
                      required: true,
                      message: i18n.t("deliveryZone.shipPriceHint"),
                    },
                  ]}
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    addonAfter="¥"
                    style={{ width: 180 }}
                  />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item
            name="enabled"
            label={i18n.t("deliveryZone.enabled")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Space>

        <Form.Item
          name="starting_price"
          label={i18n.t("deliveryZone.startingPrice")}
          rules={[
            {
              required: true,
              message: i18n.t("deliveryZone.startingPriceHint"),
            },
          ]}
          extra={i18n.t("deliveryZone.startingPriceExtra")}
        >
          <InputNumber
            min={0}
            step={0.01}
            precision={2}
            addonAfter="¥"
            style={{ width: 180 }}
          />
        </Form.Item>

        <Form.Item label={i18n.t("deliveryZone.boundary")}>
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            {i18n.t("deliveryZone.levelHintNested")}
          </Text>
          <DeliveryZoneBoundaryMap
            boundary={boundary}
            onChange={setBoundary}
            shopLongitude={shopLongitude}
            shopLatitude={shopLatitude}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default EditDeliveryZoneForm;
